import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { useFieldArray } from "react-hook-form"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import { toTaxPercent } from "src/main/Billing/Items/helpers"

import Button from "src/components/Button"
import Form from "src/components/Form"
import ReloadableWidget from "src/components/ReloadableWidget"

import { queryProducts } from "src/api/Billing/Items"

import LateFeeSettingsModal from "../LateFeeSettingsModal"
import { getAmountDisplay } from "../tabPanels/Rate/utils"
import { formatLateFeeFormData, parseStoredLateFeeSetting } from "../utils"

const LateFeeSettings = ({ useNewTaxExperience }) => {
  const { currencySymbol, marinaSlug } = useMarina()
  const [isLateFeeSettingsModalOpen, setIsLateFeeSettingsModalOpen] =
    useState(false)
  const { fields, append, remove, update } = useFieldArray({
    name: "lateFeeSettings",
  })
  const automatedLateFeeSettings = fields.filter(
    (setting) => setting.category === "automated_late_fee"
  )
  const lateFee = automatedLateFeeSettings[0]

  const {
    isFetching: isFetchingLateFeeProducts,
    isError: isErrorLateFeeProducts,
    data: lateFeeProducts,
  } = useQuery({
    queryKey: ["products", "LateFeeSettings", marinaSlug],
    queryFn: ({ pageParam = 1 }) =>
      queryProducts({
        page: pageParam,
        marinaSlug,
        availability: ["late_fees"],
      }),
    refetchOnWindowFocus: true,
  })

  const lateFeeProduct = lateFee
    ? lateFeeProducts?.find((product) => product.id === lateFee.productId)
    : null
  const lateFeeProductUnavailable =
    lateFee && lateFeeProducts && !lateFeeProduct

  const onModalClose = () => {
    setIsLateFeeSettingsModalOpen(false)
  }

  const onLateFeeSettingsSave = (data) => {
    const formattedData = formatLateFeeFormData(data)
    if (lateFee) {
      update(0, formattedData)
    } else {
      append(formattedData)
    }

    onModalClose()
  }

  const renderLateFeeCopy = () => {
    if (lateFeeProduct) {
      const displayAmount = getAmountDisplay(lateFee, currencySymbol)
      const displayAmountText = lateFee.percentage
        ? `${displayAmount} of invoice balance`
        : displayAmount
      const gracePeriodInteger = Number(lateFee.gracePeriodDays)
      const daysText = pluralize("day", Number(lateFee.gracePeriodDays))
      const displayTaxRate = useNewTaxExperience
        ? lateFeeProduct.combined_tax_rate_display
        : `${toTaxPercent(lateFeeProduct.tax_rate)}%`
      const displayTaxText =
        displayTaxRate === "0%" ? "" : ` (plus ${displayTaxRate} tax)`

      return (
        <div>{`${displayAmountText}${displayTaxText} applied after ${gracePeriodInteger} ${daysText} past due`}</div>
      )
    }
  }

  const renderLateFeeDisplay = () => {
    return (
      <>
        <div className="card flex flex-row items-center justify-between border-none bg-gray-100">
          <div className="w-3/4">
            <div className="font-semibold">{lateFee.name}</div>
            {renderLateFeeCopy()}
          </div>
          <div className="w-1/4">
            <div className="flex flex-row justify-end gap-4">
              <a
                className="cursor-pointer font-semibold"
                onClick={() => setIsLateFeeSettingsModalOpen(true)}
              >
                Edit
              </a>
              <a
                className="cursor-pointer font-semibold"
                onClick={() => remove(0)}
              >
                Remove
              </a>
            </div>
          </div>
        </div>
        {lateFeeProductUnavailable ? (
          <Form.Error>
            {
              "The configured Late fee item is no longer available, please use the 'Edit' option to adjust your late fee settings accordingly."
            }
          </Form.Error>
        ) : null}
      </>
    )
  }

  return (
    <>
      <ReloadableWidget
        isLoading={isFetchingLateFeeProducts}
        isError={isErrorLateFeeProducts}
        loadingText="Loading late fee settings ..."
      >
        {lateFee ? (
          renderLateFeeDisplay()
        ) : (
          <div className="mt-3 flex w-full">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsLateFeeSettingsModalOpen(true)}
            >
              Add Late Fee
            </Button>
          </div>
        )}
      </ReloadableWidget>
      {isLateFeeSettingsModalOpen ? (
        <LateFeeSettingsModal
          isOpen={isLateFeeSettingsModalOpen}
          lateFeeProducts={lateFeeProducts}
          onSave={onLateFeeSettingsSave}
          onClose={onModalClose}
          selectedLateFee={parseStoredLateFeeSetting(lateFee)}
          useNewTaxExperience={useNewTaxExperience}
          isFetchingLateFeeProducts={isFetchingLateFeeProducts}
        />
      ) : null}
    </>
  )
}

LateFeeSettings.propTypes = {
  useNewTaxExperience: PropTypes.bool.isRequired,
}

export default LateFeeSettings
